export type AttachmentHandlingMode = 'process' | 'awareness';

export type AttachmentTypeMode = AttachmentHandlingMode | 'inherit';

export type AttachmentTypeId =
  | 'audio'
  | 'document'
  | 'image'
  | 'video'
  | 'sticker'
  | 'location';

export const ATTACHMENT_TYPE_IDS: AttachmentTypeId[] = [
  'audio',
  'document',
  'image',
  'video',
  'sticker',
  'location',
];

export interface AttachmentTypePolicyDraft {
  mode: AttachmentTypeMode;
  instruction: string;
}

export interface AttachmentPolicyDraft {
  defaultMode: AttachmentHandlingMode;
  instruction: string;
  types: Record<AttachmentTypeId, AttachmentTypePolicyDraft>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isAttachmentTypeId = (value: string): value is AttachmentTypeId =>
  ATTACHMENT_TYPE_IDS.includes(value as AttachmentTypeId);

const isHandlingMode = (value: unknown): value is AttachmentHandlingMode =>
  value === 'process' || value === 'awareness';

const createDefaultTypeDraft = (): AttachmentTypePolicyDraft => ({
  mode: 'inherit',
  instruction: '',
});

export const createDefaultAttachmentPolicyDraft = (): AttachmentPolicyDraft => ({
  defaultMode: 'process',
  instruction: '',
  types: ATTACHMENT_TYPE_IDS.reduce(
    (accumulator, typeId) => {
      accumulator[typeId] = createDefaultTypeDraft();
      return accumulator;
    },
    {} as Record<AttachmentTypeId, AttachmentTypePolicyDraft>,
  ),
});

const readOptionalString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

export const parseAttachmentPolicyDraft = (
  customFields: string | null | undefined,
): AttachmentPolicyDraft => {
  const draft = createDefaultAttachmentPolicyDraft();

  if (!customFields?.trim()) {
    return draft;
  }

  try {
    const parsed = JSON.parse(customFields) as unknown;
    if (!isRecord(parsed)) {
      return draft;
    }

    const policy = parsed.attachmentPolicy;
    if (!isRecord(policy)) {
      return draft;
    }

    if (isHandlingMode(policy.defaultMode)) {
      draft.defaultMode = policy.defaultMode;
    }

    draft.instruction = readOptionalString(policy.instruction);

    const types = policy.types;
    if (!isRecord(types)) {
      return draft;
    }

    for (const [rawTypeId, rawTypePolicy] of Object.entries(types)) {
      if (!isAttachmentTypeId(rawTypeId) || !isRecord(rawTypePolicy)) {
        continue;
      }

      const mode = isHandlingMode(rawTypePolicy.mode) ? rawTypePolicy.mode : 'inherit';
      draft.types[rawTypeId] = {
        mode,
        instruction: readOptionalString(rawTypePolicy.instruction),
      };
    }
  } catch {
    return draft;
  }

  return draft;
};

export const serializeAttachmentPolicy = (
  draft: AttachmentPolicyDraft,
): Record<string, unknown> | undefined => {
  const globalInstruction = draft.instruction.trim();
  const types: Record<string, { mode: AttachmentHandlingMode; instruction?: string }> = {};

  for (const typeId of ATTACHMENT_TYPE_IDS) {
    const typeDraft = draft.types[typeId];
    const typeInstruction = typeDraft.instruction.trim();
    const resolvedMode = typeDraft.mode === 'inherit' ? draft.defaultMode : typeDraft.mode;

    if (typeDraft.mode !== 'inherit') {
      const entry: { mode: AttachmentHandlingMode; instruction?: string } = {
        mode: typeDraft.mode,
      };
      if (typeInstruction) {
        entry.instruction = typeInstruction;
      }
      types[typeId] = entry;
      continue;
    }

    if (typeInstruction) {
      types[typeId] = {
        mode: resolvedMode,
        instruction: typeInstruction,
      };
    }
  }

  const isDefaultPolicy =
    draft.defaultMode === 'process'
    && !globalInstruction
    && Object.keys(types).length === 0;

  if (isDefaultPolicy) {
    return undefined;
  }

  const payload: Record<string, unknown> = {
    defaultMode: draft.defaultMode,
  };

  if (globalInstruction) {
    payload.instruction = globalInstruction;
  }

  if (Object.keys(types).length > 0) {
    payload.types = types;
  }

  return payload;
};

export const buildCustomFieldsValue = (
  existingValue: string | null | undefined,
  resourceListInstruction: string,
  attachmentPolicy: AttachmentPolicyDraft,
): string | null => {
  const trimmedInstruction = resourceListInstruction.trim();
  const serializedAttachmentPolicy = serializeAttachmentPolicy(attachmentPolicy);
  const hasExistingValue = Boolean(existingValue?.trim());

  let parsed: Record<string, unknown> | null = null;
  if (existingValue?.trim()) {
    try {
      const existingParsed = JSON.parse(existingValue) as unknown;
      parsed = isRecord(existingParsed) ? { ...existingParsed } : null;
    } catch {
      parsed = null;
    }
  }

  const nextValue = parsed ? { ...parsed } : {};

  if (trimmedInstruction) {
    nextValue.resourceListInstruction = trimmedInstruction;
  } else {
    delete nextValue.resourceListInstruction;
  }

  if (serializedAttachmentPolicy) {
    nextValue.attachmentPolicy = serializedAttachmentPolicy;
  } else {
    delete nextValue.attachmentPolicy;
  }

  if (!parsed && hasExistingValue) {
    nextValue.legacyCustomFields = existingValue!.trim();
  }

  return Object.keys(nextValue).length > 0 ? JSON.stringify(nextValue) : null;
};
